import { Button } from "@shopify/polaris"
import {
	GoogleAuthProvider,
	signInWithPopup,
	signInWithCredential,
} from "firebase/auth"
import React, { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { EmailAndPasswordForm } from "../components/EmailAndPasswordForm.jsx"
import { LanguageSelector } from "../components/LanguageSelector.jsx"
import { LoadingToast } from "../components/LoadingToast.jsx"
import Logo from "../components/Logo.jsx"
import { auth } from "../firebase.js"
import { useAuthState } from "react-firebase-hooks/auth"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useTrackFacebookEvent } from "../hooks/useTrackFacebookEvent.js"
import GoogleOneTapLogin from "react-google-one-tap-login"
import { usePostHog } from "posthog-js/react"
import {
	captureMetaAdsEvent,
	META_ADS_EVENTS,
} from "../utils/posthogMetaAds.js"

export async function completeAutostoreOnboarding() {
	try {
		const idToken = await auth.currentUser?.getIdToken()
		if (!idToken) return false
		const res = await fetch("/api/kopy/onboard", {
			method: "POST",
			headers: {
				Authorization: `Bearer ${idToken}`,
				"Content-Type": "application/json",
			},
		})
		if (!res.ok) return false
		const data = await res.json()
		if (data?.ok && data?.fromAutostore) {
			window.location.href = data.next || "/subscription"
			return true
		}
		return false
	} catch (e) {
		console.error("completeAutostoreOnboarding error", e)
		return false
	}
}

const Login = ({ firebaseAuth, mcpAuthorizeMode = false }) => {
	const posthog = usePostHog()
	const [loadingAuth, setLoadingAuth] = useState(true)
	const { t } = useTranslation()
	const [user] = useAuthState(auth)
	const navigate = useNavigate()
	const [emailOverride, setEmailOverride] = useState(null)
	const trackFacebookEvent = useTrackFacebookEvent()
	const [searchParams, setSearchParams] = useSearchParams()

	const logInWithGoogle = async () => {
		await signInWithPopup(firebaseAuth, new GoogleAuthProvider())
			.then((res) => {
				if (res?.user?.email) {
					onGoogleLoginSuccess(res.user.email)
				}
			})
			.catch((err) => {
				console.error("Error login with Google", err)
			})
	}

	const onGoogleOneTap = async (response) => {
		await signInWithCredential(
			firebaseAuth,
			GoogleAuthProvider.credential(response.credential),
		)
			.then((res) => {
				if (res?.user?.email) {
					onGoogleLoginSuccess(res.user.email)
				}
			})
			.catch((err) => {
				console.error("Error login with Google One Tap", err)
			})
	}

	const onGoogleLoginSuccess = async (userEmail) => {
		if (window.tolt?.signup) {
			window.tolt.signup(userEmail)
		}
		posthog?.capture(
			"user_logged_in_v2",
			{ method: "google" },
			{ transport: "sendBeacon" },
		)

		// In the MCP OAuth flow the parent (Shell) takes over once the Firebase
		// session exists, so don't navigate/reload away from /mcp-authorize.
		if (mcpAuthorizeMode) return

		const done = await completeAutostoreOnboarding()
		if (done) return

		if (
			localStorage.getItem("eapp_shop") &&
			localStorage.getItem("eapp_handoff")
		) {
			window.location.href = "/savestore?migration"
		} else {
			window.location.reload()
		}
	}

	useEffect(async () => {
		if (user) {
			// Let Shell complete the MCP authorization instead of navigating
			// into the app.
			if (mcpAuthorizeMode) return

			if (
				localStorage.getItem("eapp_shop") &&
				localStorage.getItem("eapp_handoff")
			) {
				navigate("/savestore?migration")
			} else {
				navigate("/")
			}
		} else {
			if (firebaseAuth) {
				setLoadingAuth(false)

				const googleQueryParam = searchParams.has("google")
				const emailQueryParam = searchParams.get("email")

				if (googleQueryParam) {
					logInWithGoogle()
				}

				if (emailQueryParam) {
					setEmailOverride(emailQueryParam)
				}
			}
		}
	}, [firebaseAuth, user])

	useEffect(() => {
		posthog?.capture("user_visits_login_page_v2")
		captureMetaAdsEvent(posthog, META_ADS_EVENTS.VIEW_CONTENT)
		trackFacebookEvent("ViewContent")
	}, [])

	// Add a number every day since 2024-04-10
	const daysSince = Math.floor(
		(new Date() - new Date(2024, 3, 8)) / 1000 / 60 / 60 / 24,
	)
	const hours = new Date().getHours()
	const fakeUsers = 1000 + daysSince * 24 + hours * 2

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				padding: "0rem 1rem",
			}}
		>
			<Logo lessMargin={true} />

			<div
				style={{
					marginTop: "2rem",
					marginBottom: "1rem",
					background: "white",
					borderRadius: "0.6rem",
					boxShadow:
						"0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)",
					width: "400px",
				}}
			>
				<div
					style={{
						padding: "0.9rem 1rem",
						borderBottom: "1.2px solid #ddd",
						width: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "start",
						gap: "0.5rem",
						color: "#444",
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="1.3rem"
						height="1.3rem"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="M5.85 17.1q1.275-.975 2.85-1.538T12 15q1.725 0 3.3.563t2.85 1.537q.875-1.025 1.363-2.325T20 12q0-3.325-2.337-5.663T12 4Q8.675 4 6.337 6.337T4 12q0 1.475.488 2.775T5.85 17.1ZM12 13q-1.475 0-2.488-1.012T8.5 9.5q0-1.475 1.012-2.488T12 6q1.475 0 2.488 1.012T15.5 9.5q0 1.475-1.012 2.488T12 13Zm0 9q-2.075 0-3.9-.788t-3.175-2.137q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22Z"
						></path>
					</svg>
					<h1
						style={{
							fontSize: "1.3rem",
							lineHeight: "1rem",
							fontWeight: "600",
						}}
					>
						{t("login.login")}
					</h1>
				</div>

				<div
					style={{
						padding: "3rem",
						display: "flex",
						flexDirection: "column",
						gap: "1rem",
					}}
				>
					{loadingAuth && (
						<LoadingToast position="relative">
							{t("common.loading")}...
						</LoadingToast>
					)}

					{!loadingAuth && (
						<Button onClick={logInWithGoogle}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.5rem",
								}}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="0.98rem"
									height="1rem"
									viewBox="0 0 256 262"
								>
									<path
										fill="#4285F4"
										d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
									></path>
									<path
										fill="#34A853"
										d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
									></path>
									<path
										fill="#FBBC05"
										d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
									></path>
									<path
										fill="#EB4335"
										d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
									></path>
								</svg>
								<span
									style={{
										fontSize: "0.85rem",
										fontWeight: "500",
										color: "#555",
									}}
								>
									{t("login.sign_in_with_google")}
								</span>
							</div>
						</Button>
					)}

					{!loadingAuth && (
						<div
							style={{
								display: "flex",
								gap: "0.5rem",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<div
								style={{
									borderTop: "1px solid #ccc",
									width: "100px",
									minHeight: "1px",
								}}
							></div>
							<span style={{ fontSize: "0.8rem" }}>
								{t("login.or")}
							</span>
							<div
								style={{
									borderTop: "1px solid #ccc",
									width: "100px",
									minHeight: "1px",
								}}
							></div>
						</div>
					)}

					{!loadingAuth && (
						<EmailAndPasswordForm
							firebaseAuth={firebaseAuth}
							emailOverride={emailOverride}
						/>
					)}
				</div>

				<div style={{ padding: "1rem", lineHeight: "1.1" }}>
					<svg
						style={{
							background: "#15b141",
							color: "#fff",
							padding: "0.2rem",
							borderRadius: "100%",
							display: "inline-block",
							verticalAlign: "middle",
						}}
						width="1.2rem"
						height="1.2rem"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="M18 11h-2q-.425 0-.712-.288T15 10t.288-.712T16 9h2V7q0-.425.288-.712T19 6t.713.288T20 7v2h2q.425 0 .713.288T23 10t-.288.713T22 11h-2v2q0 .425-.288.713T19 14t-.712-.288T18 13zm-9 1q-1.65 0-2.825-1.175T5 8t1.175-2.825T9 4t2.825 1.175T13 8t-1.175 2.825T9 12m-8 6v-.8q0-.85.438-1.562T2.6 14.55q1.55-.775 3.15-1.162T9 13t3.25.388t3.15 1.162q.725.375 1.163 1.088T17 17.2v.8q0 .825-.587 1.413T15 20H3q-.825 0-1.412-.587T1 18"
						></path>
					</svg>
					<span style={{ marginLeft: "0.3rem", fontSize: "0.75rem" }}>
						<Trans
							i18nKey="login.social_proof"
							values={{
								peopleCount: fakeUsers.toLocaleString("en-us"),
							}}
							components={{ b: <b></b> }}
						></Trans>
					</span>
				</div>
			</div>

			<div style={{ maxWidth: "400px" }}>
				<p
					style={{
						fontSize: "0.7rem",
						color: "#777",
						textAlign: "center",
					}}
				>
					{t("login.tos_part_1")}{" "}
					<a
						href="https://www.kopy.app/terms-conditions"
						target="_blank"
						rel="noreferrer"
						style={{ color: "#000" }}
					>
						{t("login.tos_part_2")}
					</a>{" "}
					{t("login.tos_part_3")}{" "}
					<a
						href="https://www.kopy.app/private-policy"
						target="_blank"
						rel="noreferrer"
						style={{ color: "#000" }}
					>
						{t("login.tos_part_4")}
					</a>
				</p>
			</div>

			<div style={{ marginTop: "1.5rem" }}>
				<LanguageSelector />
			</div>

			<GoogleOneTapLogin
				onError={(error) => console.error(error)}
				googleAccountConfigs={{
					client_id:
						"364326054726-llj27s2kbjpjjf381bjucs3upi4lmj7i.apps.googleusercontent.com",
					cancel_on_tap_outside: false,
					callback: (response) => onGoogleOneTap(response),
				}}
			/>

			<style>{`
				html {
					font-size: 18px;
				}
				.Polaris-Label__Text {
					font-size: 0.7rem !important;
					line-height: 1 !important;
				}
				.Polaris-Button.Polaris-Button--plain span {
					font-size: 0.7rem !important;
				}
			`}</style>
		</div>
	)
}

export default Login
